import { Component, ViewChild } from '@angular/core';
import { SplashScreen } from '@ionic-native/splash-screen';
import { StatusBar } from '@ionic-native/status-bar';
import { Storage } from '@ionic/storage';
import { AlertController, LoadingController, NavController, Platform } from 'ionic-angular';
import { Subscription } from '../models/subscription';
import { User } from '../models/user';
import { HomePage } from '../pages/home/home';
import { LoginPage } from '../pages/login/login';
import { RegisterPage } from '../pages/register/register';
import { Api } from '../providers/api';
import { NotFoundError } from '../providers/api/errors/NotFoundError';
import { GetSessionResponse } from '../providers/api/responses/GetSessionResponse';
import { DirectoryService } from '../providers/directory-service';
import { GoogleMapsService } from '../providers/google-maps';
import { SubscriptionService } from '../providers/subscription-service';
import { UserService } from '../providers/user-service';
import { FirebaseAnalytics } from '@ionic-native/firebase-analytics';
import { DesktopService } from "../providers/desktop-service";
import { IntroductionPage } from "../pages/introduction/introduction";
import { LicencePromptPage } from '../pages/desktop/licence-prompt/licence-prompt';
import { SplitViewPage } from "../pages/desktop/split-view/split-view";
import { License } from '../models/License';
import { NetworkService } from "../providers/network-service";

@Component({
    templateUrl: 'app.html'
})
export class MyApp {

    @ViewChild('navCtrl') private navCtrl: NavController;

    public isDesktop: boolean;
    public online: boolean = true;

    constructor(
        private api: Api,
        private alertCtrl: AlertController,
        private analytics: FirebaseAnalytics,
        private directoryService: DirectoryService,
        private googleMaps: GoogleMapsService,
        private loadingCtrl: LoadingController,
        private platform: Platform,
        private splashScreen: SplashScreen,
        private statusBar: StatusBar,
        private storage: Storage,
        private subscriptionService: SubscriptionService,
        private userService: UserService,
        private desktopService: DesktopService,
        private networkService: NetworkService,
    ) {
        this.platform.ready().then(() => {
            this.statusBar.styleLightContent();
        });
    }

    public ngAfterViewInit() {
        this.directoryService.setControllers({
            alertCtrl: this.alertCtrl,
            loadingCtrl: this.loadingCtrl,
            navCtrl: this.navCtrl
        });

        this.directoryService.setPages({
            loginPage: LoginPage,
            registerPage: RegisterPage,
        });

        this.googleMaps.init();

        this.platform.ready().then(async () => {
            this.isDesktop = this.desktopService.isDesktop();

            let session = await this.prepareSession();
            let subscription = await this.prepareSubscription(session ? session.subscription : null);
            let user = await this.prepareUser(session ? session.user : null);

            if (this.isDesktop) {
                this.networkService.networkStatusChange$.subscribe((online: boolean) => {
                    this.online = online;
                });

                const { license } = session || { license: null };
                if (this.islicenseValid(license)) {
                    await this.navCtrl.setRoot(SplitViewPage, { leftPane: HomePage });
                } else {
                    await this.navCtrl.setRoot(LicencePromptPage);
                }
            } else if (user) {
                await this.navCtrl.setRoot(HomePage)
            } else {
                if (await this.isFirstRun()) {
                    await this.navCtrl.setRoot(IntroductionPage);
                } else {
                    await this.navCtrl.setRoot(LoginPage);
                }
            }

            // Okay, so the platform is ready and our plugins are available.
            // Here you can do any higher level native things you might need.
            this.splashScreen.hide();
        });
    }

    public async prepareSession(): Promise<GetSessionResponse> {
        try {
            let apiToken = await this.storage.get('api_token');
            this.api.setToken(apiToken);
        } catch (error) {
            if (error instanceof NotFoundError) return null;
            console.error(error);
        }

        try {
            return await this.api.getSession().toPromise();
        } catch (error) {
            return null;
        }
    }

    public async prepareSubscription(subscription: Subscription): Promise<Subscription> {
        try {
            await this.subscriptionService.setAndStore(subscription);
            return subscription;
        } catch (error) {
            return null;
        }
    }

    public async prepareUser(user: User): Promise<User> {
        try {
            await this.userService.setAndStore(user);
            return user;
        } catch (error) {
            return null;
        }
    }

    private async isFirstRun(): Promise<boolean> {
        let firstRun: boolean = await this.storage.get('firstRun');

        return firstRun === null || firstRun === undefined;
    }

    private islicenseValid(license: License|null = null): boolean {
        return license && license.is_active;
    }
}




// WEBPACK FOOTER //
// ./src/app/app.component.ts